import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Phone, Mail, MapPin, Clock, ArrowRight } from 'lucide-react';
import { ContactForm } from '../components/ContactForm';
import { GoogleMap } from '../components/GoogleMap';

export default function ContactPage() {
  return (
    <div className="min-h-screen pt-20" data-testid="contact-page">
      <Helmet>
        <title>Contact - Cours de Musique à Périgueux | École de Musique Pour Tous</title>
        <meta name="description" content="Contactez l'École de Musique Pour Tous à Périgueux. Réservez votre cours d'essai gratuit de piano, guitare, chant ou éveil musical. Réponse sous 24h." />
        <link rel="canonical" href="https://ecoledemusiqueperigueux.fr/contact" />
      </Helmet>
      {/* Hero */}
      <section className="py-16 bg-gradient-to-br from-cream to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-block px-4 py-2 bg-burgundy/10 text-burgundy rounded-full text-sm font-semibold mb-4">
              Contactez-nous à Périgueux
            </span>
            <h1 className="font-dancing text-5xl sm:text-6xl text-burgundy mb-6">
              Réservez votre cours d'essai gratuit
            </h1>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto font-nunito">
              Envie de commencer le piano, la guitare, le chant ou l'éveil musical à Périgueux ? 
              Remplissez le formulaire ci-dessous. Nous vous répondons sous 24h.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="lg:col-span-1 space-y-6"
            >
              {/* Phone */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-burgundy/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-burgundy" />
                  </div>
                  <div>
                    <h3 className="font-nunito font-bold text-gray-800 mb-1">Téléphone</h3>
                    <a
                      href="tel:+33649523778"
                      className="text-burgundy font-semibold text-lg hover:text-coral transition-colors"
                      data-testid="contact-phone-link"
                    >
                      06 49 52 37 78
                    </a>
                    <p className="text-gray-500 text-sm mt-1">Appel ou SMS</p>
                  </div>
                </div>
              </div>

              {/* Email */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-coral/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-coral" />
                  </div>
                  <div>
                    <h3 className="font-nunito font-bold text-gray-800 mb-1">Email</h3>
                    <a
                      href="mailto:musiquepourtous24@gmail.com"
                      className="text-coral font-semibold hover:text-burgundy transition-colors break-all"
                      data-testid="contact-email-link"
                    >
                      musiquepourtous24@gmail.com
                    </a>
                    <p className="text-gray-500 text-sm mt-1">Réponse sous 24h</p>
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-teal" />
                  </div>
                  <div>
                    <h3 className="font-nunito font-bold text-gray-800 mb-1">Adresse</h3>
                    <p className="text-gray-700">
                      2 Rue Ludovic Trarieux<br />
                      24000 Périgueux
                    </p>
                    <p className="text-gray-500 text-sm mt-1">Quartier Clos Chassaing</p>
                    <a
                      href="https://maps.google.com/?q=2+Rue+Ludovic+Trarieux,+24000+Périgueux"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-teal text-sm font-semibold mt-2 hover:text-burgundy transition-colors"
                      data-testid="contact-directions-link"
                    >
                      Voir l'itinéraire
                      <ArrowRight className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </div>

              {/* Hours */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-burgundy/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-burgundy" />
                  </div>
                  <div>
                    <h3 className="font-nunito font-bold text-gray-800 mb-1">Horaires</h3>
                    <p className="text-gray-700">Sur rendez-vous</p>
                    <p className="text-gray-500 text-sm mt-1">
                      Cours en journée, soir, mercredi et samedi
                    </p>
                  </div>
                </div>
              </div>

              {/* Quick CTA */}
              <a
                href="tel:+33649523778"
                className="flex items-center justify-center gap-2 w-full px-6 py-4 bg-burgundy text-white rounded-2xl font-bold hover:bg-burgundy-600 transition-all shadow-lg hover:shadow-xl"
                data-testid="contact-quick-call-btn"
              >
                <Phone className="w-5 h-5" />
                Appeler maintenant
              </a>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="lg:col-span-2"
            >
              <div className="bg-white rounded-3xl shadow-lg p-8">
                <h2 className="font-dancing text-3xl text-burgundy mb-2">
                  Demande de renseignements
                </h2>
                <p className="text-gray-600 mb-6 font-nunito">
                  Remplissez ce formulaire et nous vous recontactons rapidement pour organiser votre cours d'essai gratuit.
                </p>
                <ContactForm variant="full" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="font-dancing text-4xl text-burgundy mb-4">
              Où nous trouver ?
            </h2>
            <p className="text-gray-600 font-nunito">
              Les cours ont lieu au 2 Rue Ludovic Trarieux, 24000 Périgueux, 
              dans le quartier Clos Chassaing.
            </p>
          </div>
          <GoogleMap className="h-96" />
        </div>
      </section>

      {/* FAQ Quick */}
      <section className="py-16 bg-cream">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-dancing text-3xl text-burgundy mb-4">
            Questions fréquentes
          </h2>
          <div className="bg-white rounded-2xl p-6 shadow-sm text-left mb-6">
            <h4 className="font-nunito font-bold text-gray-800 mb-2">Le premier cours est-il vraiment gratuit ?</h4>
            <p className="text-gray-600 text-sm">
              Oui, le premier cours est entièrement gratuit et sans engagement. 
              C'est l'occasion de faire connaissance et de découvrir notre méthode.
            </p>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-sm text-left mb-6">
            <h4 className="font-nunito font-bold text-gray-800 mb-2">Sous combien de temps me rappellerez-vous ?</h4>
            <p className="text-gray-600 text-sm">
              Nous nous efforçons de répondre sous 24h. Si votre demande est urgente, 
              n'hésitez pas à nous appeler directement au 06 49 52 37 78.
            </p>
          </div>
          <a
            href="/cours"
            className="inline-flex items-center gap-2 text-burgundy font-semibold hover:text-coral transition-colors"
          >
            Voir tous nos cours
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </section>
    </div>
  );
}
